import type { CodingAnswerOption } from '@seen/core/codings'
import { cn } from '@seen/ui'
import { IconCheck } from '@tabler/icons-react'
import type { JSX, Ref } from 'react'
import {
  answerSegmentClass,
  answerSegmentDividerClass,
} from '#src/components/rich-text-editor/ObservationNode/observationChip.ts'

export interface SegmentedAnswersProps {
  // One answer (radios) or any that apply (checkboxes). Buttons rather than
  // native inputs: a button group reads better inside a line of note text.
  role: 'radio' | 'checkbox'
  options: readonly CodingAnswerOption[]
  isPicked: (option: CodingAnswerOption) => boolean
  onClick: (option: CodingAnswerOption) => void
  isDisabled?: (option: CodingAnswerOption) => boolean
  // Names the group for assistive technology.
  label?: string
  // The first answer, for moving focus to it.
  firstAnswerRef?: Ref<HTMLButtonElement>
}

// A few short answers side by side in one divided control, the picked ones
// ticked.
export function SegmentedAnswers({
  role,
  options,
  isPicked,
  onClick,
  isDisabled,
  label,
  firstAnswerRef,
}: SegmentedAnswersProps): JSX.Element {
  return (
    <span
      role={role === 'radio' ? 'radiogroup' : 'group'}
      aria-label={label}
      className={answerSegmentClass()}
    >
      {options.map((option, index) => {
        const picked = isPicked(option)
        return (
          <span
            key={`${option.system ?? ''}|${option.code}`}
            className="inline-flex items-center gap-1.5"
          >
            {index > 0 ? (
              <span className={answerSegmentDividerClass()} aria-hidden />
            ) : null}
            <button
              ref={index === 0 ? firstAnswerRef : undefined}
              type="button"
              role={role}
              aria-checked={picked}
              disabled={isDisabled?.(option) ?? false}
              onClick={() => onClick(option)}
              className={cn(
                'inline-flex items-center gap-0.5 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-zinc-950 disabled:cursor-not-allowed disabled:opacity-60',
                picked ? 'text-blue-600' : 'text-foreground'
              )}
            >
              {picked ? (
                <IconCheck className="size-3.5 shrink-0" aria-hidden />
              ) : null}
              {option.display}
            </button>
          </span>
        )
      })}
    </span>
  )
}
